/**
 * ============================================================================
 * JWT 토큰 디코딩 유틸리티
 * ============================================================================
 * 
 * 주요 함수 요약
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * 1. decodeJWT         - JWT 토큰을 디코딩하여 Payload 반환
 * 2. isTokenExpired    - 토큰 만료 여부 확인
 * 3. getRoleFromToken  - 토큰에서 사용자 역할 추출 (소문자 반환)
 * 4. getUserNoFromToken - 토큰에서 사용자 번호 추출 (문자열 반환)
 * 
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * JWT 토큰 구조
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * JWT 토큰은 3개의 Base64 인코딩된 부분으로 구성:
 * "header.payload.signature"
 * 
 * 예시:
 * eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.       <- Header
 * eyJ1c2VyX25vIjo0LCJyb2xlIjoiUFJPRkVTU09SIi... <- Payload (사용하는 부분)
 * i0WSkNtHm6SXWkRQXd97s_fbQ6GzeB_tLVD9HVPqXQ0  <- Signature
 * 
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * Payload 디코딩 결과
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * {
 *   "user_no": 4,              // 사용자 번호 (숫자)
 *   "role": "PROFESSOR",       // 역할 (대문자: "STUDENT" | "PROFESSOR")
 *   "exp": 1798599597,         // 만료 시간 (Unix timestamp)
 *   "iat": 1767063597          // 발급 시간 (Unix timestamp)
 * }
 * 
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * 사용 예시
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * import { decodeJWT, getRoleFromToken } from '@/core/utils/jwt';
 * 
 * // 토큰에서 역할 가져오기 (소문자로 반환)
 * const role = getRoleFromToken(token);  // 'student' | 'professor' | null
 * 
 * // 전체 페이로드 가져오기
 * const payload = decodeJWT(token);
 * console.log(payload.user_no);  // 4
 * console.log(payload.role);     // 'PROFESSOR'
 * 
 * // 브라우저 콘솔에서 직접 확인
 * const token = document.cookie.split(';').find(c => c.includes('front_auth_token'))?.split('=')[1];
 * const payload = JSON.parse(atob(token.split('.')[1]));
 * console.log(payload);
 * 
 * ============================================================================
 */

/**
 * JWT Payload 타입
 * 
 * @property user_no  - 사용자 번호 (API에서는 숫자, 문자열로도 올 수 있음)
 * @property user_id  - 사용자 ID (user_no와 동일하거나 다를 수 있음)
 * @property role     - 역할 (API: 대문자 'STUDENT'|'PROFESSOR', 변환 후: 소문자)
 * @property sub      - Subject (일부 JWT에서 사용자명으로 사용)
 * @property exp      - 만료 시간 (Unix timestamp, 초 단위)
 * @property iat      - 발급 시간 (Unix timestamp, 초 단위)
 */
export interface JWTPayload {
  user_no?: number | string;
  user_id?: string;
  role?: 'STUDENT' | 'PROFESSOR' | 'student' | 'professor';
  sub?: string;
  exp?: number;
  iat?: number;
  [key: string]: any;
}

/**
 * JWT 토큰을 디코딩합니다
 */
export const decodeJWT = (token: string): JWTPayload | null => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) {
      return null;
    }

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    // console.error('JWT 디코딩 실패:', error);
    return null;
  }
};

/**
 * JWT 토큰이 만료되었는지 확인합니다
 */
export const isTokenExpired = (token: string): boolean => {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return true;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  return payload.exp < currentTime;
};

/**
 * JWT 토큰 만료까지 남은 시간(초) 반환
 * 만료됐으면 음수, 유효하면 양수
 */
export const getSecondsUntilExpiry = (token: string): number | null => {
  const payload = decodeJWT(token);
  if (!payload || payload.exp === undefined) {
    return null;
  }
  const currentTime = Math.floor(Date.now() / 1000);
  return payload.exp - currentTime;
};

/**
 * JWT 토큰에서 사용자 역할을 추출합니다
 * API에서 대문자로 오는 경우 (STUDENT, PROFESSOR) 소문자로 변환합니다
 */
export const getRoleFromToken = (token: string): 'student' | 'professor' | null => {
  const payload = decodeJWT(token);
  if (!payload) {
    return null;
  }

  // 대소문자 구분 없이 처리
  const role = payload.role?.toLowerCase();
  if (role === 'student' || role === 'professor') {
    return role;
  }

  return null;
};

/**
 * JWT 토큰에서 사용자 번호를 추출합니다
 * @returns 문자열로 변환된 사용자 번호 또는 null
 */
export const getUserNoFromToken = (token: string): string | null => {
  const payload = decodeJWT(token);
  if (!payload) {
    return null;
  }

  // user_no가 숫자일 수 있으므로 문자열로 변환
  if (payload.user_no !== undefined) {
    return String(payload.user_no);
  }
  
  return payload.user_id || null;
};









